/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */

// Guaranteed Payment Exposure by Originating Attorney

// @mui material components
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DataTable from "examples/Tables/DataTable";

// React
import { useMemo, useState } from "react";

import { useMaterialUIController } from "context";
import { getGuaranteedExposure } from "layouts/report/exposure";
import { exportExposureToExcel } from "./exportExposure";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const formatAmount = (amount) => currencyFormatter.format(amount || 0);

const exposureColumns = [
    { Header: "Originating Atty", accessor: "originatingAttorney", align: "left" },
    { Header: "Matter", accessor: "matter", align: "left" },
    { Header: "Attorney(s) Paid", accessor: "paidAttorneys", align: "left" },
    { Header: "Total Exposure", accessor: "totalExposure", align: "right" },
];

function GuaranteedExposureCard({
    rows,
    mattersRows,
    activityKeyedData,
    pKeyeddata,
    loading,
    dateRangeLabel,
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [exporting, setExporting] = useState(false);

    const exposure = useMemo(
        () => getGuaranteedExposure(rows, mattersRows, activityKeyedData, pKeyeddata),
        [rows, mattersRows, activityKeyedData, pKeyeddata]
    );

    const exposureRows = useMemo(() => {
        const tableRows = [];

        exposure.attorneys.forEach((attorney) => {
            attorney.matters.forEach((matter, index) => {
                tableRows.push({
                    originatingAttorney: (
                        <MDTypography variant="button" fontWeight="medium">
                            {index === 0 ? attorney.name : ""}
                        </MDTypography>
                    ),
                    matter: (
                        <MDBox display="flex" flexDirection="column">
                            <MDTypography variant="button" fontWeight="regular">
                                {matter.displayNumber}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                                {`Invoice(s): ${matter.invoices.join(", ")}`}
                            </MDTypography>
                        </MDBox>
                    ),
                    paidAttorneys: (
                        <MDBox display="flex" flexDirection="column">
                            {matter.paidAttorneys.map((paid) => (
                                <MDTypography key={paid.name} variant="button" fontWeight="regular">
                                    {`${paid.name} — ${formatAmount(paid.amount)}`}
                                </MDTypography>
                            ))}
                        </MDBox>
                    ),
                    totalExposure: (
                        <MDTypography variant="button" fontWeight="medium">
                            {formatAmount(matter.total)}
                        </MDTypography>
                    ),
                });
            });

            // Subtotal row. `isTotal` is not a column, so react-table ignores it —
            // it is only there for `rowSx` below to pick the row out.
            tableRows.push({
                isTotal: true,
                originatingAttorney: (
                    <MDTypography variant="button" fontWeight="bold" color="info" textTransform="uppercase">
                        {`${attorney.name} total`}
                    </MDTypography>
                ),
                matter: "",
                paidAttorneys: "",
                totalExposure: (
                    <MDTypography variant="h6" fontWeight="bold" color="info">
                        {formatAmount(attorney.total)}
                    </MDTypography>
                ),
            });
        });

        return tableRows;
    }, [exposure]);

    // Lift the subtotal rows off the matter rows: tinted band, accent rule above,
    // and no hairline below so each attorney reads as one block.
    const rowSx = (row) =>
        row?.isTotal
            ? {
                backgroundColor: darkMode ? "rgba(73, 163, 241, 0.16)" : "rgba(26, 115, 232, 0.07)",
                "& td": {
                    borderTop: `2px solid ${darkMode ? "rgba(73, 163, 241, 0.6)" : "rgba(26, 115, 232, 0.45)"}`,
                    borderBottom: "none",
                    paddingTop: "14px",
                    paddingBottom: "14px",
                },
            }
            : undefined;

    const handleExport = async () => {
        setExporting(true);
        try {
            await exportExposureToExcel(exposure, dateRangeLabel);
        } catch (error) {
            console.error("Failed to export the exposure table", error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Card>
            <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
            >
                <MDTypography variant="h6" color="white">
                    Guaranteed Payment Exposure by Originating Attorney
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={2}>
                    <MDTypography variant="h6" color="white">
                        {`Total: ${formatAmount(exposure.grandTotal)}`}
                    </MDTypography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={loading || exporting || exposureRows.length === 0}
                        sx={{
                            color: "#fff",
                            borderColor: "rgba(255, 255, 255, 0.7)",
                            whiteSpace: "nowrap",
                            "&:hover": {
                                borderColor: "#fff",
                                backgroundColor: "rgba(255, 255, 255, 0.12)",
                            },
                            "&.Mui-disabled": {
                                color: "rgba(255, 255, 255, 0.5)",
                                borderColor: "rgba(255, 255, 255, 0.3)",
                            },
                        }}
                    >
                        {exporting ? "Exporting..." : "Excel"}
                    </Button>
                </MDBox>
            </MDBox>

            {!loading ? (
                <MDBox pt={3}>
                    {exposureRows.length > 0 ? (
                        <DataTable
                            table={{ columns: exposureColumns, rows: exposureRows }}
                            isSorted={false}
                            entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
                            showTotalEntries={false}
                            noEndBorder
                            rowSx={rowSx}
                        />
                    ) : (
                        <MDBox
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="120px"
                        >
                            <MDTypography variant="button" color="text">
                                No guaranteed payments are outstanding on uncollected invoices.
                            </MDTypography>
                        </MDBox>
                    )}
                </MDBox>
            ) : (
                <MDBox
                    pt={3}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <p>loading...</p>
                </MDBox>
            )}
        </Card>
    );
}

export default GuaranteedExposureCard;
